import { useId } from 'react';

/**
 * Illustration d'accueil — des galets posés, et l'onde qu'ils ont laissée.
 *
 * Elle occupe le vide de la première venue, quand il n'y a encore aucun dossier à montrer.
 * Le sujet n'est pas décoratif par hasard : un empilement de galets tient parce que chaque
 * pierre trouve son équilibre sur la précédente, sans que l'une écrase l'autre. C'est
 * l'image la plus juste dont dispose un produit qui parle à deux parents séparés — et elle
 * ne représente ni famille, ni enfant, ni maison, trois images qu'un parent en instance
 * peut recevoir comme un reproche.
 *
 * **Elle est écrite dans le langage graphique de la marque**, celui de `Picto` : des formes
 * pleines sans contour, deux teintes seulement, et le détail creusé en négatif. Les pastels
 * (`icon-blue`, `icon-green`) sont ceux que les tokens réservent aux grandes tailles
 * décoratives — à 20 px ils disparaîtraient, à 320 px ils posent une ambiance sans jamais
 * tirer l'œil hors du texte qu'ils accompagnent.
 *
 * **Le détachement est découpé, pas peint** — le procédé du logo, pour la même raison. Les
 * deux pastels ne se distinguent que par la teinte (1,02:1 l'un contre l'autre) : deux
 * galets qui se touchent formeraient une seule tache, et un daltonien ne verrait qu'un
 * caillou informe. La silhouette élargie de chaque forme est donc retirée de celle qui est
 * derrière, par un masque. L'écart est un trou, pas un liseré : il prend la couleur de ce
 * qu'il y a derrière, et l'illustration tient donc aussi bien sur la carte blanche du thème
 * clair que sur son anthracite.
 *
 * `aria-hidden` sans réserve : le dessin ne dit rien que le texte de la carte ne dise, et
 * un lecteur d'écran n'a pas à entendre parler de cailloux.
 */
export function Galets() {
  // Deux illustrations sur une même page partageraient sinon un identifiant de masque, et
  // la seconde effacerait les découpes de la première. Les deux-points que `useId` produit
  // sont retirés : `url(#:r0:)` n'est pas honoré partout.
  const cle = useId().replace(/:/g, '');
  const sansGalets = `galets-decoupe-${cle}`;
  const sansMilieu = `galets-decoupe-milieu-${cle}`;
  const sansHaut = `galets-decoupe-haut-${cle}`;

  return (
    <svg
      viewBox="0 0 320 110"
      className="illustration-galets"
      aria-hidden="true"
      focusable="false"
    >
      {/* Les trois masques, du plus enveloppant au plus local. Chaque forme est découpée
          par la silhouette élargie de celle qui vient devant elle, et d'elle seule. */}
      <mask id={sansGalets} maskUnits="userSpaceOnUse" x="0" y="0" width="320" height="110">
        <rect width="320" height="110" fill="#FFFFFF" />
        {[BAS, MILIEU, HAUT, GAUCHE, DROIT].map((galet, rang) => (
          <path key={rang} d={contour(galet, ECART)} fill="#000000" />
        ))}
      </mask>
      <mask id={sansMilieu} maskUnits="userSpaceOnUse" x="0" y="0" width="320" height="110">
        <rect width="320" height="110" fill="#FFFFFF" />
        <path d={contour(MILIEU, ECART)} fill="#000000" />
      </mask>
      <mask id={sansHaut} maskUnits="userSpaceOnUse" x="0" y="0" width="320" height="110">
        <rect width="320" height="110" fill="#FFFFFF" />
        <path d={contour(HAUT, ECART)} fill="#000000" />
      </mask>

      {/* L'onde : trois anneaux concentriques, aplatis comme une surface vue de biais.
          Ils passent derrière les galets, qui les découpent au passage. */}
      <g mask={`url(#${sansGalets})`} fill="var(--couleur-icon-blue, #D2E3F7)">
        {ONDES.map((onde, rang) => (
          <path key={rang} d={anneau(onde)} fillRule="evenodd" />
        ))}
      </g>

      {/* L'empilement, du bas vers le haut. Le décalage est diagonal et non concentrique —
          trois galets parfaitement centrés dessineraient une tour, c'est-à-dire une
          hiérarchie ; décalés, ils dessinent un équilibre. */}
      <path
        d={contour(BAS)}
        mask={`url(#${sansMilieu})`}
        fill="var(--couleur-icon-green, #D9E7D6)"
      />
      <path
        d={contour(MILIEU)}
        mask={`url(#${sansHaut})`}
        fill="var(--couleur-icon-blue, #D2E3F7)"
      />
      <path d={contour(HAUT)} fill="var(--couleur-icon-green, #D9E7D6)" />

      {/* Deux galets à l'écart, de part et d'autre. Ils rompent la symétrie : un empilement
          seul et centré se lirait comme un pictogramme posé au milieu d'un cadre. */}
      <path d={contour(GAUCHE)} fill="var(--couleur-icon-green, #D9E7D6)" />
      <path d={contour(DROIT)} fill="var(--couleur-icon-blue, #D2E3F7)" />
    </svg>
  );
}

interface Forme {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

/** La largeur du vide entre deux formes — la même valeur, partout, comme dans le logo. */
const ECART = 3;

/**
 * Les galets. Larges et bas plutôt que ronds : une pierre roulée par l'eau est aplatie, et
 * c'est cet aplatissement qui la fait tenir sur la suivante.
 */
const BAS: Forme = { cx: 160, cy: 72, rx: 40, ry: 15 };
const MILIEU: Forme = { cx: 154, cy: 48, rx: 28, ry: 12 };
const HAUT: Forme = { cx: 163, cy: 26, rx: 18, ry: 10 };
const GAUCHE: Forme = { cx: 58, cy: 84, rx: 22, ry: 8 };
const DROIT: Forme = { cx: 262, cy: 80, rx: 28, ry: 10 };

/** Les anneaux de l'onde, du plus large au plus proche de la pile. */
const ONDES: { forme: Forme; epaisseur: number }[] = [
  { forme: { cx: 160, cy: 82, rx: 150, ry: 20 }, epaisseur: 4 },
  { forme: { cx: 160, cy: 82, rx: 112, ry: 15 }, epaisseur: 4 },
  { forme: { cx: 160, cy: 82, rx: 80, ry: 11 }, epaisseur: 3.5 },
];

/**
 * Une ellipse pleine, éventuellement dilatée de `marge` — deux arcs d'un demi-tour chacun,
 * la seule façon de décrire une ellipse fermée dans un `<path>`.
 */
function contour({ cx, cy, rx, ry }: Forme, marge = 0): string {
  const [a, b] = [rx + marge, ry + marge];
  return `M ${cx - a} ${cy} a ${a} ${b} 0 1 0 ${2 * a} 0 a ${a} ${b} 0 1 0 ${-2 * a} 0 Z`;
}

/**
 * Un anneau : l'ellipse extérieure, puis l'intérieure dans le même tracé. C'est
 * `fillRule="evenodd"` qui creuse la seconde dans la première — le trou laisse passer le
 * fond, quel qu'il soit, là où un anneau peint aurait figé la couleur de la carte.
 *
 * L'épaisseur est retirée aux deux rayons, et non mise à l'échelle : proportionnelle, elle
 * aurait réduit à rien l'épaisseur verticale d'une ellipse aussi aplatie que celle-ci.
 */
function anneau({ forme, epaisseur }: { forme: Forme; epaisseur: number }): string {
  return `${contour(forme)} ${contour(forme, -epaisseur)}`;
}
